import { Keyword, ModelType } from '@livv/models';
import { getThematicPlanLink } from '@livv/utils/helpers';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { Box, Button, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { Translate } from 'next-translate';
import { FC, useState } from 'react';
import { getKeywordFilter } from '@components/Icicle/utils';
import { PlainLink } from '@components/Links';
import { CustomInfiniteHits, InstantSearch } from '@components/search';
import SideModal from '@components/SideModal';
import { INDEX_NAME } from '@utils/consts/search';

const DRAWER_QUERY = 'showLinkedAnalysis';

interface LinkedAnalysisModalProps {
    analysisTitle: string;
    isCardFocused: boolean;
    keyword?: Keyword;
    onButtonClick: () => void;
    pathKeywords: string[];
    t: Translate;
    theme: string;
    treeTitle: string;
}

const LinkedAnalysisModal: FC<LinkedAnalysisModalProps> = ({
    analysisTitle,
    isCardFocused,
    keyword,
    onButtonClick,
    pathKeywords,
    treeTitle,
    t,
    theme,
}) => {
    const { asPath } = useRouter();
    const [drawerOpened, setDrawerOpened] = useState<boolean>(
        isCardFocused && asPath.includes(DRAWER_QUERY),
    );
    const pathTitles = [treeTitle, ...pathKeywords];

    const drawerFilters =
        pathTitles &&
        `modelType:${ModelType.ANALYSIS} AND ${getKeywordFilter(
            pathTitles,
        )} AND (NOT isPublished:false)`;

    return (
        <>
            <Button
                onClick={() => {
                    onButtonClick();
                    setDrawerOpened(true);
                }}
                startIcon={<CompareArrowsIcon />}
                sx={{ bgcolor: 'white', fontSize: 'inherit', height: 40, mr: 2, mt: 1 }}
                type="button"
                variant="ghost"
            >
                {t('relatedAnalysis')}
            </Button>
            <SideModal
                onClose={() => {
                    setDrawerOpened(false);
                }}
                open={drawerOpened}
            >
                <Box sx={{ mr: 2 }}>
                    <Typography sx={{ color: 'grey.100', fontWeight: 'medium', mb: 1.5 }}>
                        {t('linkedAnalysis.title', { analysisTitle })}
                    </Typography>
                    <PlainLink
                        data-test-id="thematic-plan-link"
                        href={getThematicPlanLink(
                            theme,
                            INDEX_NAME,
                            keyword,
                            ModelType.DECISION,
                            true,
                        )}
                        sx={{ display: 'block', mb: 3, textAlign: 'right' }}
                        openInNewTab
                    >
                        {t('linkedAnalysis.link')}
                    </PlainLink>
                    <InstantSearch>
                        <CustomInfiniteHits filters={drawerFilters} />
                    </InstantSearch>
                </Box>
            </SideModal>
        </>
    );
};

export default LinkedAnalysisModal;
